import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { UserStatusText } from '../userStatusBadge/UserStatusBadge'
import { UserCardActions, UserCardActionHandler } from '../userCardActions/UserCardActions'

import Avatar from '../../../../ui/Avatar'
import { Button } from '../../../../buttons/Button'
import { Buttons } from '../../../../buttons/Buttons'

import { ProfileMetadata } from '../../../../../types/user/userProfile'
import {
  formatUserJoinedAt,
  getUserDisplayName,
} from '../../../../../utils/users/user'

type UserCardLayout = 'page' | 'inline'

type UserCardProps = {
  user: ProfileMetadata
  roles: ReactNode
  picture?: string | null
  layout?: UserCardLayout
  onAction?: UserCardActionHandler
  actionLoading?: boolean
}

const UserCardContent = ({
  user,
  roles,
  picture,
  layout,
  onAction,
  actionLoading,
}: UserCardProps) => {
  const { t, i18n } = useTranslation('users')

  const displayName = getUserDisplayName(user)
  const hasName = displayName !== user.email

  return (
    <div className="user-card-content">
      <div className="ph-row ph-between user-card-header">
        <div className="ph-row user-card-header-main">
          <Avatar
            name={displayName}
            image={picture}
            size={layout === 'page' ? 'lg' : 'sm'}
            className="user-card-header-avatar"
          />
          <div className="user-card-heading">
            <h3>{displayName}</h3>
            {hasName && <div className="mono tiny muted">{user.email}</div>}
          </div>
        </div>
        {layout !== 'page' && onAction && (
          <div className="user-card-menu">
            <UserCardActions
              user={user}
              onAction={onAction}
              actionLoading={actionLoading}
            />
          </div>
        )}
      </div>

      <div className="user-card-fields">
        <div className="user-card-field">
          <div className="tiny muted">{t('users.details.roles')}</div>
          <div className="user-card-roles">{roles}</div>
        </div>
        <div className="ph-row ph-between user-card-detail">
          <span className="muted">{t('users.details.status')}</span>
          <UserStatusText status={user.status} locked={user.locked} />
        </div>
        <div className="ph-row ph-between user-card-detail">
          <span className="muted">{t('users.details.joined')}</span>
          <span className="mono">
            {formatUserJoinedAt(user.joinedAt, i18n.language)}
          </span>
        </div>
        {user.phoneNumber && (
          <div className="ph-row ph-between user-card-detail">
            <span className="muted">{t('users.details.phone')}</span>
            <span className="mono">{user.phoneNumber}</span>
          </div>
        )}
      </div>

      {layout === 'page' && (
        <Buttons
          direction="ph-col"
          className="user-card-actions user-card-view-actions"
        >
          <Button
            block={true}
            label={t('users.actions.send-message')}
            variant="dark"
            size="md"
            title={t('users.actions.unavailable')}
            disabled={true}
          />
        </Buttons>
      )}
    </div>
  )
}

export const UserCard = ({
  user,
  roles,
  picture,
  layout = 'inline',
  onAction,
  actionLoading,
}: UserCardProps) => {
  const content = (
    <UserCardContent
      user={user}
      roles={roles}
      picture={picture}
      layout={layout}
      onAction={onAction}
      actionLoading={actionLoading}
    />
  )

  if (layout === 'page') {
    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="ph-card user-card user-card-page">{content}</div>
        </div>
      </div>
    )
  }

  return <div className="ph-card user-card">{content}</div>
}
